/**
 * ============================================================================
 * useSignDispositionMutation — Phase 6a frontend (T-P6a-W3-03)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/disposition/tasks/{id}/sign — record a QA Manager or
 *          Doc Controller signature on a disposition task. The body carries
 *          `auth_method=WEBAUTHN` — only phishing-resistant 2FA is accepted
 *          for disposition (DESTROY / ARCHIVE / EXPORT are irreversible).
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type {
  DispositionTask,
  SignerRole,
} from './useDispositionTasksQuery';

export type DispositionAuthMethod = 'WEBAUTHN';

export interface SignDispositionInput {
  taskId: string;
  role: SignerRole;
  attestationText: string;
  // Phase 6a: WebAuthn is mocked by signature-svc (returns an opaque assertion
  // id). When the real adapter lands, this stays the same — the WebAuthn flow
  // happens before this call and the assertion ref is forwarded here.
  webauthnAssertionId?: string;
}

export function useSignDispositionMutation(): UseMutationResult<
  DispositionTask,
  Error,
  SignDispositionInput
> {
  return useMutation<DispositionTask, Error, SignDispositionInput>({
    mutationFn: ({ taskId, role, attestationText, webauthnAssertionId }) =>
      apiFetch<DispositionTask>(
        `/api/v1/disposition/tasks/${encodeURIComponent(taskId)}/sign`,
        {
          method: 'POST',
          body: {
            role,
            auth_method: 'WEBAUTHN' as DispositionAuthMethod,
            attestation_text: attestationText,
            webauthn_assertion_id: webauthnAssertionId ?? null,
          },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
